import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatAuDate } from "@/lib/date";
import { ArrowLeft, RefreshCcw, CheckCircle2, XCircle } from "lucide-react";

export default function Vacancies() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [data, setData] = useState(null);
  const [activeDay, setActiveDay] = useState(null);

  async function load() {
    try {
      const [ev, vac] = await Promise.all([
        api.get(`/public/events/${slug}`),
        api.get(`/public/events/${slug}/vacancies`),
      ]);
      setEvent(ev.data);
      setData(vac.data);
      setActiveDay((prev) => prev || (vac.data?.days?.[0]?.date ?? null));
    } catch {
      setData(false);
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [slug]);

  if (data === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center border border-black/10 p-10">
          <h1 className="font-display text-2xl font-black">Event not available</h1>
          <Link to="/" className="mt-6 inline-block brand-btn px-4 py-2 text-sm">VOLMEE Home</Link>
        </div>
      </div>
    );
  }
  if (!data || !event) return <div className="p-10 text-sm text-zinc-500">Loading vacancies…</div>;

  const days = data.days || [];
  const day = days.find((d) => d.date === activeDay) || days[0];

  // Overall event totals
  let total = 0, grandVacant = 0;
  for (const d of days) {
    for (const s of d.sessions || []) {
      total += (s.roles || []).length;
      grandVacant += (s.vacant_roles || []).length;
    }
  }
  const grandFilled = total - grandVacant;

  // Per-selected-day totals
  let dayTotal = 0, dayVacant = 0;
  if (day) {
    for (const s of day.sessions || []) {
      dayTotal += (s.roles || []).length;
      dayVacant += (s.vacant_roles || []).length;
    }
  }
  const dayFilled = dayTotal - dayVacant;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 border-b border-black/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to={`/events/${slug}`} className="font-display text-2xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="text-zinc-600 hover:text-black" data-testid="refresh-vacancies-btn"><RefreshCcw className="h-4 w-4" /></button>
            <Link to={`/events/${slug}/register`} data-testid="register-cta" className="brand-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium">Register</Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <Link to={`/events/${slug}`} className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black"><ArrowLeft className="h-4 w-4" /> {event.name}</Link>
        <div className="mt-6 border border-black/10 bg-white p-8 md:p-10 shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#002FA7]">Live vacancies</div>
              <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">What's still open.</h1>
              <p className="mt-3 text-sm text-zinc-600">Read only view — Register at top to select a role.</p>
            </div>
            <div className="grid min-w-[260px] grid-cols-3 gap-2 border border-black/10 bg-[#F4F4F5] p-3" data-testid="overall-totals">
              <div className="col-span-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Whole event</div>
              <SummaryCard label="Total slots" value={total} />
              <SummaryCard label="Filled" value={grandFilled} color="text-emerald-700" />
              <SummaryCard label="Vacant" value={grandVacant} color="text-[#002FA7]" />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2 border-b border-black/10">
            {days.map((d) => (
              <button key={d.date} onClick={() => setActiveDay(d.date)} data-testid={`vacancy-tab-${d.date}`}
                className={`px-4 py-2 font-display text-lg font-black tracking-tighter border-b-2 -mb-px transition-colors ${day?.date === d.date ? "border-[#002FA7] text-black" : "border-transparent text-zinc-500 hover:text-black"}`}>
                {formatAuDate(d.date)}
              </button>
            ))}
          </div>

          {day ? (
            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="day-totals">
              <SummaryCard label={`${formatAuDate(day.date)} — total`} value={dayTotal} />
              <SummaryCard label="Filled" value={dayFilled} color="text-emerald-700" />
              <SummaryCard label="Vacant" value={dayVacant} color="text-[#002FA7]" />
            </div>
          ) : null}

          {day ? (
            <div className="mt-6 space-y-4">
              {(day.sessions || []).map((s) => {
                const vacantSet = new Set(s.vacant_roles || []);
                return (
                  <div key={s.name} className="border border-black/10" data-testid={`session-block-${s.name.replace(/\s+/g, "")}`}>
                    <div className="flex flex-wrap items-baseline gap-3 border-b border-black/5 bg-[#F4F4F5] px-4 py-2">
                      <span className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Event element</span>
                      <span className="font-display text-base font-black tracking-tighter">{s.name}</span>
                      {s.start || s.end ? <span className="font-mono text-xs text-zinc-500">{s.start || "?"} – {s.end || "?"}</span> : null}
                      {(s.location || "").trim() ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location.trim())}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`session-location-link-${s.name.replace(/\s+/g, "")}`}
                          className="font-mono text-xs text-[#002FA7] underline decoration-dotted hover:decoration-solid"
                          title="Open in Google Maps"
                        >📍 {s.location.trim()}</a>
                      ) : null}
                      <span className="ml-auto font-mono text-[10px] text-zinc-500">{(s.roles || []).length} role{(s.roles || []).length !== 1 ? "s" : ""}</span>
                    </div>
                    <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3">
                      {(s.roles || []).map((r) => {
                        const vacant = vacantSet.has(r);
                        return (
                          <li key={r} data-testid={`vacancy-cell-${s.name.replace(/\s+/g, "")}-${r.replace(/\s+/g, "")}`}
                            className={`flex items-center gap-2 border px-3 py-2.5 text-xs ${vacant ? "border-[#002FA7]/25 bg-blue-50 text-[#002FA7]" : "border-black/10 bg-white text-zinc-400"}`}>
                            {vacant ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 shrink-0" />}
                            <span className="min-w-0 flex-1 truncate font-semibold">{r}</span>
                            <span className="shrink-0 font-mono text-[10px]">{vacant ? "Available" : "Filled"}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
              {!(day.sessions || []).length ? <p className="text-sm italic text-zinc-500">No sessions on this day.</p> : null}
            </div>
          ) : <p className="mt-6 text-sm italic text-zinc-500">No days set up yet.</p>}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="border border-black/10 p-4">
      <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-black tracking-tighter ${color || "text-black"}`}>{value}</div>
    </div>
  );
}
